import React, { useEffect, useState } from 'react'
import { Button, Card, Col, DatePicker, Form, Row, Spin, Table } from 'antd'
import BaseListing from 'src/BaseListings'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import WidgetBankAccountSelector from 'src/widgets/Selectors/WidgetBankAccountSelector'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import { wtFormatRoundedValue } from 'src/helpers/commons/utils'
import { wtGetDateTimeFormat } from 'src/helpers/commons/moment'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment'

const UntaggedReceiptsReport = () => {
  const Base = new BaseListing('untagged_receipts_report')
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [accountOptions, setAccountOptions] = useState([])
  const [bankAccounts, setBankAccounts] = useState([])
  const [reportData, setReportData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selectedAccountId, setSelectedAccountId] = useState(0)
  const [selectedAccountName, setSelectedAccountName] = useState('')
  const [selectedBankAccountId, setSelectedBankAccountId] = useState(0)
  const [selectedBankAccountName, setSelectedBankAccountName] = useState('')
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [totalAmount, setTotalAmount] = useState(0)

  useEffect(() => {
    wtApiCall(
      'reports/untagged_receipts/accounts',
      {},
      'get',
      (response) => {
        setAccountOptions(response?.data || [])
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
      },
    )

    Base.dropdownData('bank_accounts', {}, [setBankAccounts], 1)
  }, [])

  const buildParams = (page = currentPage, size = pageSize) => ({
    _page_index: page,
    _page_size: size,
    account_id: selectedAccountId,
    bank_account_id: selectedBankAccountId,
    from_date: fromDate,
    to_date: toDate,
  })

  const fetchReport = (page = 1, size = pageSize) => {
    if (!fromDate || !toDate) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    if (!selectedAccountId) {
      lzNotifyErrorMessageFromResult('Please select advance account')
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/untagged_receipts',
      buildParams(page, size),
      'get',
      (response) => {
        setReportData(response?.data?.data || [])
        setTotalRecords(response?.data?.total || 0)
        setCurrentPage(response?.data?.current_page || page)
        setPageSize(response?.data?.per_page || size)
        setTotalAmount(response?.data?.total_amount || 0)
        setIsLoading(false)
      },
      (error) => {
        setReportData([])
        setTotalRecords(0)
        setTotalAmount(0)
        setIsLoading(false)
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }

  const exportToExcel = async () => {
    if (!fromDate || !toDate) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    if (!selectedAccountId) {
      lzNotifyErrorMessageFromResult('Please select advance account')
      return
    }

    setIsLoading(true)
    await wtApiPostWithContentTypeHandling('reports/untagged_receipts/export', buildParams(1, pageSize))
    setIsLoading(false)
  }

  const fetchAllRowsForPdf = () =>
    new Promise((resolve, reject) => {
      wtApiCall(
        'reports/untagged_receipts',
        {
          ...buildParams(1, Math.max(totalRecords || reportData.length || 1, 1)),
          _page_index: 1,
          _page_size: Math.max(totalRecords || reportData.length || 1, 1),
        },
        'get',
        (response) => {
          resolve(response?.data?.data || [])
        },
        (error) => {
          reject(error)
        },
      )
    })

  const printPdf = async () => {
    if (!fromDate || !toDate) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    if (!selectedAccountId) {
      lzNotifyErrorMessageFromResult('Please select advance account')
      return
    }

    setIsLoading(true)

    try {
      const rows = await fetchAllRowsForPdf()

      if (!rows.length) {
        setIsLoading(false)
        lzNotifyErrorMessageFromResult('No data found for PDF print')
        return
      }

      const doc = new jsPDF()
      const totalPagesExp = '{total_pages_count_string}'
      const logo = 'assets/wil-icon.png'
      const computedTotalAmount = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0)

      const drawPageChrome = () => {
        const pageSize = doc.internal.pageSize
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()
        let pageNo = doc.internal.getNumberOfPages()
        let pageText = 'Page ' + pageNo

        if (typeof doc.putTotalPages === 'function') {
          pageText += ' of ' + totalPagesExp
        }

        doc.setFontSize(11)
        doc.setTextColor('#3c4b64')
        doc.setFont('Helvetica', 'bold')
        doc.addImage(logo, 'PNG', 5, 4, 25, 25)
        doc.line(32, 6, 32, 25)
        doc.text('WAH', 35, 10, { align: 'left' })
        doc.text('INDUSTRIES', 35, 15)
        doc.text('LIMITED', 35, 20)
        doc.setFontSize(9)
        doc.text('www.wahindustries.com', 35, 25)
        doc.setFontSize(11)
        doc.text('UNDEFINED RECEIPTS REPORT', pageWidth - 72, 12, { align: 'left' })
        doc.text(`AS OF ${moment().format('MMMM D, YYYY')}`, pageWidth - 72, 19, { align: 'left' })
        doc.line(10, 30, pageWidth - 10, 30)

        doc.setFontSize(10)
        doc.text('This report is printed through ERP at ', 40, pageHeight - 10)
        doc.setTextColor('#0202f7')
        doc.setFont('Helvetica', 'bold')
        doc.text(moment().format('YYYY-MM-DD HH:mm:ss'), 102, pageHeight - 10)

        doc.setTextColor('black')
        doc.setFont('Helvetica', 'normal')
        doc.setFontSize(9)
        doc.line(10, pageHeight - 14, pageWidth - 10, pageHeight - 14)
        doc.text(pageText, pageWidth - 10, pageHeight - 7, { align: 'right' })
      }

      const summaryMap = rows.reduce((accumulator, row) => {
        const bankName = row.bank_account_name || 'N/A'

        if (!accumulator[bankName]) {
          accumulator[bankName] = {
            bankName,
            undefinedReceiptsValue: 0,
            totalReceipts: 0,
          }
        }

        accumulator[bankName].undefinedReceiptsValue += Number(row.amount || 0)
        accumulator[bankName].totalReceipts += 1

        return accumulator
      }, {})

      const summaryRows = Object.values(summaryMap)
        .sort((left, right) => right.undefinedReceiptsValue - left.undefinedReceiptsValue)
        .map((summaryRow, index) => ([
          index + 1,
          summaryRow.bankName,
          wtFormatRoundedValue(summaryRow.undefinedReceiptsValue || 0, '', '', 0),
          summaryRow.totalReceipts,
        ]))

      summaryRows.push([
        '',
        'Total',
        wtFormatRoundedValue(computedTotalAmount || 0, '', '', 0),
        rows.length,
      ])

      const tableRows = rows.map((row, index) => {
        const receiptDate = row.voucher_date || ''
        const daysElapsed = receiptDate ? moment().diff(moment(receiptDate), 'days') : 0

        return [
          index + 1,
          `${row.type_short_name || 'BRV'} - ${row.company_voucher_no || ''} - ${row.voucher_no || ''}`,
          row.bank_account_name || '',
          wtFormatRoundedValue(row.amount || 0, '', '', 0),
          receiptDate,
          daysElapsed,
        ]
      })

      tableRows.push([
        '',
        'Total',
        '',
        wtFormatRoundedValue(computedTotalAmount || 0, '', '', 0),
        '',
        '',
      ])

      doc.setFont('Helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor('#3c4b64')
      doc.text('Summary', 10, 38)

      doc.autoTable({
        head: [[
          'Sr',
          'Bank Name',
          'Undefined Receipts (Value)',
          'Total Receipts',
        ]],
        body: summaryRows,
        startY: 44,
        margin: { top: 40, bottom: 20, left: 10, right: 10 },
        theme: 'grid',
        styles: {
          fontSize: 8.5,
          halign: 'center',
          valign: 'middle',
          lineColor: '#3c4b64',
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [60, 75, 100],
          fontStyle: 'bold',
          fontSize: 9,
          lineColor: '#3c4b64',
          lineWidth: 0.2,
        },
        columnStyles: {
          0: { cellWidth: 12 },
          1: { cellWidth: 105, halign: 'left' },
          2: { cellWidth: 40, halign: 'right' },
          3: { cellWidth: 28, halign: 'center' },
        },
        didDrawPage: drawPageChrome,
        didParseCell: function (data) {
          if (data.section === 'body' && data.column.index === 2) {
            data.cell.styles.fontStyle = 'bold'
            data.cell.styles.textColor = [2, 2, 247]
          }

          if (data.section === 'body' && data.row.index === summaryRows.length - 1) {
            data.cell.styles.fontStyle = 'bold'
            if (data.column.index === 2) {
              data.cell.styles.textColor = [2, 2, 247]
            }
          }
        },
      })

      doc.addPage()
      doc.setFont('Helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor('#3c4b64')
      doc.text('Details', 10, 38)

      doc.autoTable({
        head: [[
          'Sr',
          'Voucher #',
          'Bank Account',
          'Amount',
          'Receipt Date',
          'Days Lappsed',
        ]],
        body: tableRows,
        startY: 44,
        margin: { top: 40, bottom: 20, left: 10, right: 10 },
        theme: 'grid',
        styles: {
          fontSize: 8.5,
          halign: 'center',
          valign: 'middle',
          lineColor: '#3c4b64',
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [60, 75, 100],
          fontStyle: 'bold',
          fontSize: 9,
          lineColor: '#3c4b64',
          lineWidth: 0.2,
        },
        columnStyles: {
          0: { cellWidth: 12 },
          1: { cellWidth: 38, halign: 'left' },
          2: { cellWidth: 72, halign: 'left' },
          3: { cellWidth: 24, halign: 'right' },
          4: { cellWidth: 24 },
          5: { cellWidth: 20 },
        },
        didDrawPage: drawPageChrome,
        didParseCell: function (data) {
          if (data.section === 'body' && data.column.index === 3) {
            data.cell.styles.fontStyle = 'bold'
            data.cell.styles.textColor = [2, 2, 247]
          }

          if (data.section === 'body' && data.row.index === tableRows.length - 1) {
            data.cell.styles.fontStyle = 'bold'
            if (data.column.index === 3) {
              data.cell.styles.textColor = [2, 2, 247]
            }
          }
        },
      })

      if (typeof doc.putTotalPages === 'function') {
        doc.putTotalPages(totalPagesExp)
      }

      const pdfBlob = doc.output('blob')
      const pdfUrl = URL.createObjectURL(pdfBlob)
      window.open(pdfUrl, '_blank')
    } catch (error) {
      lzNotifyErrorMessageFromResult(error)
    }

    setIsLoading(false)
  }

  const onTableChange = (pagination) => {
    const nextPage = pagination.current || 1
    const nextPageSize = pagination.pageSize || pageSize
    setCurrentPage(nextPage)
    setPageSize(nextPageSize)
    fetchReport(nextPage, nextPageSize)
  }

  const columns = [
    {
      title: 'Date',
      dataIndex: 'voucher_date',
      key: 'voucher_date',
      width: 95,
    },
    {
      title: 'BRV Number',
      dataIndex: 'voucher_no',
      key: 'voucher_no',
      width: 190,
      render: (_, record) => (
        <a
          href={`/vouchers#view:${record.voucher_id}`}
          target="_blank"
          rel="noreferrer"
        >
          {`${record.type_short_name || 'BRV'} - ${record.company_voucher_no || ''} - ${record.voucher_no || ''}`}
        </a>
      ),
    },
    {
      title: 'Branch',
      dataIndex: 'branch_name',
      key: 'branch_name',
      width: 130,
    },
    {
      title: 'Bank Account',
      dataIndex: 'bank_account_name',
      key: 'bank_account_name',
      width: 170,
    },
    {
      title: 'Advance Account',
      dataIndex: 'advance_account_name',
      key: 'advance_account_name',
      width: 170,
    },
    {
      title: 'Narration',
      dataIndex: 'narration',
      key: 'narration',
      width: 220,
      render: (value) => value || '-',
    },
    {
      title: 'Party',
      dataIndex: 'party_status',
      key: 'party_status',
      width: 95,
      render: () => 'Undefined Customer',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (value) => <span style={{ fontWeight: 700 }}>{wtFormatRoundedValue(value, '')}</span>,
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 130,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 145,
      render: (value) => wtGetDateTimeFormat(value),
    },
  ]

  return (
    <div
      className="container-fluid"
      style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh', fontFamily: 'Helvetica' }}
    >
      <Form layout="vertical" form={form} onFinish={() => fetchReport(1, pageSize)}>
        <Card
          style={{ width: '100%' }}
          title={<span style={{ fontSize: '26px', marginLeft: '40%' }}>Untagged Receipts</span>}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={5}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                style={{ width: '100%', height: 35 }}
                onChange={(value) => {
                  if (value && value.length === 2) {
                    setFromDate(value[0].format('YYYY-MM-DD'))
                    setToDate(value[1].format('YYYY-MM-DD'))
                  } else {
                    setFromDate(null)
                    setToDate(null)
                  }
                  setReportData([])
                  setTotalAmount(0)
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>Advance Account: *</label>
              <WidgetSelector
                selectedValue={selectedAccountId}
                displayValue={selectedAccountName}
                options={accountOptions}
                showCloudIcon={false}
                onChange={(value) => {
                  setSelectedAccountId(value?.value || 0)
                  setSelectedAccountName(value?.label || '')
                  setReportData([])
                  setTotalAmount(0)
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <label>Bank Account:</label>
              <WidgetBankAccountSelector
                value={selectedBankAccountId}
                displayValue={selectedBankAccountName}
                bank_accounts={bankAccounts}
                onChange={(value) => {
                  setSelectedBankAccountId(value?.value || 0)
                  setSelectedBankAccountName(value?.label || '')
                  setReportData([])
                  setTotalAmount(0)
                }}
                reloadDropdownData={() => Base.dropdownData('bank_accounts', {}, [setBankAccounts], 1)}
                size="sm"
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Button style={{ marginTop: 30, marginRight: 8 }} type="primary" htmlType="submit" disabled={isLoading}>
                Search
              </Button>
              <Button
                style={{ marginTop: 30, marginRight: 8, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading}
              >
                Export
              </Button>
              <Button
                style={{ marginTop: 30, backgroundColor: '#d32f2f', color: '#fff' }}
                onClick={printPdf}
                disabled={isLoading}
              >
                Print PDF
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>

      <Card style={{ marginTop: 30 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={reportData}
            rowKey="detail_id"
            bordered
            scroll={{ x: 1500, y: 600 }}
            summary={() =>
              reportData.length > 0 ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  <Table.Summary.Cell index={4}></Table.Summary.Cell>
                  <Table.Summary.Cell index={5}></Table.Summary.Cell>
                  <Table.Summary.Cell index={6} style={{ fontWeight: 700 }}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right" style={{ fontWeight: 700 }}>
                    {wtFormatRoundedValue(totalAmount, '')}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8}></Table.Summary.Cell>
                  <Table.Summary.Cell index={9}></Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null
            }
            pagination={{
              current: currentPage,
              pageSize,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100, 200],
              position: ['bottomCenter'],
            }}
            onChange={onTableChange}
          />
        </Spin>
      </Card>
    </div>
  )
}

export default UntaggedReceiptsReport
